/**
 * The page an extraction was read from, with **where each field was read
 * drawn on it** (engine W15, FR-E15, FR-E41, plan D27, D32; T15.17, T15.18).
 *
 * A PDF is drawn by PDF.js on a `<canvas>`, its parsing in a worker
 * (`pdf.worker.ts`); an image (PNG, JPEG, WebP) by the browser's own decoder,
 * as an `<img>` from the bytes; a **TIFF** — which Chrome, Edge and Firefox do
 * not draw — page by page from the PNG the engine's sandbox draws of it (W16,
 * T16.14, C-E12, D44; `page-drawings.ts`): the page **as it lies**, every page
 * of a multi-page TIFF, each fetched when it is first shown. Which of the three
 * is decided by **the version the extraction read** (T16.20), never by the
 * current one, which may be another kind of file. Each mark is a box over the page: the
 * evidence's box is integers in **hundredths of a PDF point from the page's
 * top-left** (C-E6), placed by proportion against the page's size in the
 * extraction's `pages[]` (T15.17) — the only way to place one on an image,
 * whose resolution the engine chose. A PDF read before `pages[]` existed is
 * placed by pdf.js's own size, as before.
 *
 * **A scanned page is turned before its boxes are drawn** (T15.18): the engine
 * read it turned upright and straightened, and its boxes are on that page
 * (FR-E15 as amended). The static rung's `scan` report says how — `turned` and
 * `deskew:<n>` — and the page is drawn turned by them (`page-geometry.ts`, where
 * the conventions are), so each box sits on the printed figure.
 *
 * The bytes are the **version the extraction read**, fetched by the same
 * logged read as any preview (purpose `view`) — the service writes the read
 * row before it hands out the address; a TIFF's page is a logged read of its
 * own, and a page once drawn is kept while the version is shown, so paging back
 * reads nothing again. PDF.js is loaded on demand (`import()`), so a host's
 * first page carries none of it; both openers are props, so a test draws
 * nothing and a host may bring its own — the image opener decodes a TIFF's
 * PNG pages too. A page that cannot be drawn says so, with the fields listed
 * beside it all the same.
 *
 * **An encrypted PDF** (W22, FR-58, D56) opens with its password **in this
 * browser only**: the one an unlock proved, from this tab's memory
 * (`password-keeper.ts`), or one typed into the box here — which reads the file
 * as any preview does and sends nothing else: no parse, no password. A typed
 * password that opens the pages is kept in the same memory; a wrong one is
 * dropped, and so is a kept one that no longer opens them. The file in the
 * store stays exactly as uploaded, protected.
 *
 * **An encrypted PDF whose password is saved** (W30, FR-67, ADR-0021) is drawn
 * by the engine, page by page, as a TIFF is — with the sealed password on the
 * server, so nothing is asked and the browser never holds the password or a
 * decrypted file. Should the service say none is saved after all (`423`), the
 * page falls back to the box.
 */
import { Alert, Button, Input, Skeleton, SkeletonRegion, TONE_TEXT, cn } from '@fiveox/ui'
import { ChevronLeft, ChevronRight, LockKeyhole } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'

import type { Box, ExtractionPage, ScanPage } from './extraction-model'
import { errorText } from './hooks'
import { passwordKeeper, useKeptPassword } from './password-keeper'
import { PREVIEW_DPI, TIFF, openImageInBrowser, usePageDrawings, type ImageHandle, type ImageOpener } from './page-drawings'
import { boxStyle, pageLayout, pageSizeOf, pageTurnOf, sourceStyle, uprightSizeOf, type PageTurn, type Size } from './page-geometry'
import { passwordSavedOf, type ProDocClient, type ProDocDocument } from './prodoc-client'

export { boxStyle, fitScale } from './page-geometry'
export { openImageInBrowser, type ImageHandle, type ImageOpener } from './page-drawings'

/** An opened PDF, as the page needs it — PDF.js's, or a test's. */
export interface PdfDocumentHandle {
  pages: number
  /** The page's size in PDF points. */
  size: (page: number) => Promise<{ width: number; height: number }>
  /** Draw the page into the canvas at `scale` CSS pixels per point. */
  render: (page: number, canvas: HTMLCanvasElement, scale: number) => Promise<void>
  destroy: () => void
}

/** W22 (D56): how a PDF is opened — with its password, for an encrypted one. Used in the browser and nowhere else. */
export interface PdfOpenOptions {
  password?: string
}

/**
 * Open a PDF's bytes. Throws `PdfRefusal`: `encrypted` for one that needs a
 * password none was given for, `wrong_password` for one the given password
 * does not open (W22), `unreadable` for one that will not open at all.
 */
export type PdfOpener = (bytes: Uint8Array, options?: PdfOpenOptions) => Promise<PdfDocumentHandle>

export class PdfRefusal extends Error {
  constructor(readonly reason: 'encrypted' | 'wrong_password' | 'unreadable') {
    super(reason)
    this.name = 'PdfRefusal'
  }
}

/** An image the browser could not decode — said, not an error to retry. */
class ImageRefusal extends Error {
  constructor() {
    super('unreadable')
    this.name = 'ImageRefusal'
  }
}

/** An image inside a PDF is not drawn beyond this many pixels — the engine's own budget per page (plan D29). */
const MAX_IMAGE_PIXELS = 50_000_000

let pdfWorker: Worker | null = null

/**
 * The default opener: PDF.js, imported when the first PDF is opened, parsing
 * in its own module worker. No WebAssembly, no XFA, a cap on embedded images —
 * the file is untrusted.
 */
export const openPdfWithPdfjs: PdfOpener = async (bytes, options = {}) => {
  if (typeof Worker === 'undefined') throw new PdfRefusal('unreadable')
  const pdfjs = await import('pdfjs-dist')
  if (!pdfWorker) {
    pdfWorker = new Worker(new URL('./pdf.worker.ts', import.meta.url), { type: 'module' })
    pdfjs.GlobalWorkerOptions.workerPort = pdfWorker
  }
  // The password goes to pdf.js in this tab and nowhere else (D56); pdf.js decrypts in its worker.
  const loading = pdfjs.getDocument({
    data: bytes,
    useWasm: false,
    enableXfa: false,
    maxImageSize: MAX_IMAGE_PIXELS,
    ...(options.password ? { password: options.password } : {}),
  })
  let doc: Awaited<typeof loading.promise>
  try {
    doc = await loading.promise
  } catch (e) {
    void loading.destroy()
    const needsPassword = e instanceof Error && e.name === 'PasswordException'
    throw new PdfRefusal(needsPassword ? (options.password ? 'wrong_password' : 'encrypted') : 'unreadable')
  }
  let task: { cancel: () => void; promise: Promise<void> } | null = null
  return {
    pages: doc.numPages,
    size: async (n) => {
      const viewport = (await doc.getPage(n)).getViewport({ scale: 1 })
      return { width: viewport.width, height: viewport.height }
    },
    render: async (n, canvas, scale) => {
      task?.cancel()
      const page = await doc.getPage(n)
      const ratio = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1
      const viewport = page.getViewport({ scale })
      canvas.width = Math.floor(viewport.width * ratio)
      canvas.height = Math.floor(viewport.height * ratio)
      const current = page.render({ canvas, viewport, transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined })
      task = current
      try {
        await current.promise
      } catch (e) {
        // A newer page replaced this one before it finished — not a failure.
        if (!(e instanceof Error && e.name === 'RenderingCancelledException')) throw e
      }
    },
    destroy: () => {
      task?.cancel()
      void loading.destroy()
    },
  }
}

/** A box drawn on the page. */
export interface PageMark {
  key: string
  /** 1-based. */
  page: number
  box: Box
  /** The field the person picked, drawn strongest; the rest of its row lighter. */
  selected: boolean
  label: string
}

const ENCRYPTED = 'This PDF is encrypted, so its pages are not shown here. Every field and where it was read is listed beside it.'
const ENCRYPTED_ASK =
  'This PDF is encrypted. Type its password to see its pages here — in this browser only; nothing is sent and the file stays protected. Every field and where it was read is listed beside it.'
const UNREADABLE = 'This PDF could not be drawn here. Every field and where it was read is listed beside it.'
const IMAGE_UNREADABLE = 'This image could not be drawn here. Every field and where it was read is listed beside it.'
const PAGE_UNREADABLE = 'This page could not be drawn here. Every field and where it was read is listed beside it.'
const PAGE_MISSING = 'This page is not in the file. Every field and where it was read is listed beside it.'

/** `pages`: a TIFF, each page the engine's drawing of it (`usePageDrawings`) — no whole-file read. */
type Source = { kind: 'pdf'; pdf: PdfDocumentHandle } | { kind: 'image'; image: ImageHandle } | { kind: 'pages' }

/** `ask`: an encrypted PDF the password box can open (D56). */
type State = { kind: 'loading' } | { kind: 'ready'; source: Source } | { kind: 'none'; reason: string; ask?: boolean } | { kind: 'error'; reason: string }

const destroy = (source: Source) => {
  if (source.kind === 'pdf') source.pdf.destroy()
  else if (source.kind === 'image') source.image.destroy()
}

const NO_PAGES: readonly ExtractionPage[] = []
const NO_SCAN: readonly ScanPage[] = []

/** `turned` (counter-clockwise) as a person says it. */
const TURN_TEXT: Record<number, string> = { 90: 'turned 90° anticlockwise', 180: 'turned upside down', 270: 'turned 90° clockwise' }

/** How a turned or straightened page is described under it — `null` for a page drawn as it lies. */
export function turnText(turn: PageTurn): string | null {
  const parts: string[] = []
  if (TURN_TEXT[turn.turned]) parts.push(TURN_TEXT[turn.turned]!)
  if (turn.deskew) parts.push(`straightened by ${(Math.abs(turn.deskew) / 100).toFixed(1)}°`)
  return parts.length ? `Shown as it was read — ${parts.join(' and ')}.` : null
}

/** An image's size in points when the extraction gives none: its pixels at `dpi` (the engine reads a scan at 300) — a display size only, never a box's. */
const imagePoints = (image: ImageHandle, dpi = 300): Size => ({ width: (image.width * 72) / dpi, height: (image.height * 72) / dpi })

export interface EvidencePageProps {
  client: ProDocClient
  doc: ProDocDocument
  /** The version the extraction read — not necessarily the current one (FR-E18). */
  versionId: string
  /**
   * That version's file kind and whether it is encrypted (T16.20) — how its
   * pages are drawn is decided by the version read, never by the current
   * one's. Omitted, the document's own: right when the version read is current.
   */
  version?: { media_type: string; encrypted: boolean; password_saved?: boolean } | null
  marks: PageMark[]
  /** The page to show — the selected field's. The person may page away from it. */
  page?: number | null
  /** The size of each page the boxes are on (T15.17). An image's boxes need it; a PDF's fall back to pdf.js's size. */
  pages?: readonly ExtractionPage[]
  /** A scan's reading, page by page — the static rung's `scan`: each page is turned by it before its boxes are drawn (T15.18). */
  scan?: readonly ScanPage[]
  openPdf?: PdfOpener
  openImage?: ImageOpener
  className?: string
}

export function EvidencePage({
  client,
  doc,
  versionId,
  version = null,
  marks,
  page = null,
  pages = NO_PAGES,
  scan = NO_SCAN,
  openPdf = openPdfWithPdfjs,
  openImage = openImageInBrowser,
  className,
}: EvidencePageProps) {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const [shown, setShown] = useState(page ?? 1)
  const [pdfSize, setPdfSize] = useState<Size | null>(null)
  const [available, setAvailable] = useState(0)
  const frame = useRef<HTMLDivElement>(null)
  const canvas = useRef<HTMLCanvasElement>(null)
  // The version read decides how it is drawn (T16.20) — a stale extraction's file may be another kind than today's.
  const mediaType = version?.media_type ?? doc.media_type
  const encrypted = version?.encrypted ?? doc.encrypted
  const isPdf = mediaType === 'application/pdf'
  // Chrome, Edge and Firefox draw no TIFF: the engine draws each page (D44), and the browser never decodes the file.
  const isTiff = mediaType === TIFF
  // W30 (FR-67): the version read has its password saved — the engine draws its pages with it, as a TIFF's.
  // `lockedOut`: the service said none is saved after all (`423`), so the box asks instead.
  const [lockedOut, setLockedOut] = useState<string | null>(null)
  const savedPages = encrypted && isPdf && (version ? version.password_saved === true : passwordSavedOf(doc)) && lockedOut !== versionId
  const drawings = usePageDrawings(client, doc.id, versionId, (isTiff && !encrypted) || savedPages, openImage)
  const drawPage = drawings.draw
  // D56: the password an unlock proved, in this tab's memory — or one typed here, being tried.
  const kept = useKeptPassword(encrypted && isPdf && !savedPages ? versionId : null)
  const [typed, setTyped] = useState<string | null>(null)
  const [wrong, setWrong] = useState(false)
  const password = encrypted && isPdf ? (typed ?? kept) : null

  useEffect(() => {
    if (page) setShown(page)
  }, [page])

  useEffect(() => {
    if (savedPages) {
      // No read of the whole file, and no password here: each page is the engine's drawing, a logged read of its own.
      setState({ kind: 'ready', source: { kind: 'pages' } })
      return
    }
    if (encrypted && !password) {
      // Said without fetching: no read is logged for a page that cannot be drawn — a PDF's box can open it.
      setState({ kind: 'none', reason: isPdf ? ENCRYPTED_ASK : ENCRYPTED, ask: isPdf })
      return
    }
    if (isTiff) {
      // No read of the whole file: each page is a logged read of its own, made when the page is shown.
      setState({ kind: 'ready', source: { kind: 'pages' } })
      return
    }
    let cancelled = false
    let opened: Source | null = null
    setState({ kind: 'loading' })
    void client.documents.versions
      .file(doc.id, versionId, 'view')
      .then((handle) => handle.blob())
      .then(async (blob): Promise<Source> => {
        if (isPdf) return { kind: 'pdf', pdf: await openPdf(new Uint8Array(await blob.arrayBuffer()), password ? { password } : undefined) }
        try {
          return { kind: 'image', image: await openImage(blob) }
        } catch {
          throw new ImageRefusal()
        }
      })
      .then((source) => {
        if (cancelled) {
          destroy(source)
          return
        }
        opened = source
        // A typed password that opened the pages is kept for this tab, so the preview opens too.
        if (typed) passwordKeeper.keep(versionId, typed)
        setTyped(null)
        setState({ kind: 'ready', source })
      })
      .catch((e) => {
        if (cancelled) return
        if (e instanceof PdfRefusal && e.reason === 'wrong_password') {
          // Never kept: a typed one is dropped and said; a kept one that no longer opens is forgotten, and the box asks.
          if (typed) {
            setTyped(null)
            setWrong(true)
          } else passwordKeeper.forget(versionId)
          setState({ kind: 'none', reason: ENCRYPTED_ASK, ask: true })
          return
        }
        if (e instanceof PdfRefusal) setState({ kind: 'none', reason: e.reason === 'encrypted' ? (isPdf ? ENCRYPTED_ASK : ENCRYPTED) : UNREADABLE, ask: e.reason === 'encrypted' && isPdf })
        else if (e instanceof ImageRefusal) setState({ kind: 'none', reason: IMAGE_UNREADABLE })
        else setState({ kind: 'error', reason: errorText(e, 'The file could not be fetched.') })
      })
    return () => {
      cancelled = true
      if (opened) destroy(opened)
    }
    // `typed` is read through `password`; a typed password is tried once, then dropped or kept.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, doc.id, encrypted, isPdf, isTiff, versionId, openPdf, openImage, attempt, password, savedPages])

  // The width the page has — measured where the browser can, a sensible width where it cannot.
  useEffect(() => {
    const el = frame.current
    if (!el) return
    setAvailable(el.clientWidth)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setAvailable(el.clientWidth))
    observer.observe(el)
    return () => observer.disconnect()
  }, [state.kind])

  const source = state.kind === 'ready' ? state.source : null
  const pdf = source?.kind === 'pdf' ? source.pdf : null
  const previewing = source?.kind === 'pages'
  // A PDF's pages are its own; a TIFF's, the pages the engine read (`pages[]`); any other image is one page.
  const pageCount = pdf ? pdf.pages : previewing ? Math.max(1, pages.length) : source?.kind === 'image' ? 1 : 0
  const current = pageCount ? Math.min(Math.max(1, shown), pageCount) : 1

  useEffect(() => {
    if (previewing) drawPage(current)
  }, [previewing, current, drawPage])

  // A TIFF's page once the engine's drawing of it is here; a scan's own image otherwise.
  const drawing = previewing ? (drawings.pages.get(current) ?? null) : null
  const refusedPages = savedPages && drawing?.kind === 'none' && drawing.reason === 'locked'

  // W30: no password is saved for the version after all — the box asks for one.
  useEffect(() => {
    if (refusedPages) setLockedOut(versionId)
  }, [refusedPages, versionId])
  const image = source?.kind === 'image' ? source.image : drawing?.kind === 'ready' ? drawing.image : null

  useEffect(() => {
    if (!pdf) return
    let cancelled = false
    void pdf.size(current).then((s) => {
      if (!cancelled) setPdfSize(s)
    })
    return () => {
      cancelled = true
    }
  }, [pdf, current])

  const turn = useMemo(() => pageTurnOf(scan, current), [scan, current])
  // The page as it lies, in points: pdf.js's size, or an image's pixels — a TIFF page's at the engine's drawing's resolution.
  const lies: Size | null = pdf ? pdfSize : image ? imagePoints(image, previewing ? PREVIEW_DPI : 300) : null
  // The page the boxes are on: `pages[]` — or, without it, the page as it lies turned upright.
  const known = pageSizeOf(pages, current)
  const upright = known ?? (lies ? uprightSizeOf(lies, turn) : null)
  const uprightWidth = upright?.width ?? 0
  const uprightHeight = upright?.height ?? 0
  const layout = useMemo(
    () => (uprightWidth > 0 && uprightHeight > 0 ? pageLayout({ width: uprightWidth, height: uprightHeight }, turn, available) : null),
    [uprightWidth, uprightHeight, turn, available],
  )
  // An image's boxes are placed only by `pages[]`, whose units the engine chose; a TIFF's page is drawn once it is here.
  const drawn = layout !== null && (pdf !== null || image !== null)
  const boxed = drawn && (pdf !== null || known !== null)
  const liesWidth = lies?.width ?? 0

  useEffect(() => {
    if (!pdf || !layout || !(liesWidth > 0) || !canvas.current) return
    // pdf.js draws the page as it lies, at the CSS pixels per point that make it fill its place before it is turned.
    void pdf.render(current, canvas.current, layout.source.width / liesWidth).catch(() => setState({ kind: 'none', reason: UNREADABLE }))
  }, [pdf, layout, liesWidth, current])

  if (state.kind === 'loading') return <Skeleton className={cn('h-[60vh] w-full rounded-lg', className)} />
  if (state.kind === 'none') {
    return (
      <div className={cn('flex min-h-[30vh] flex-col items-center justify-center gap-3 p-6 text-center', className)} data-evidence-page="none">
        <p className="text-muted-foreground max-w-sm text-sm">{state.reason}</p>
        {state.ask && (
          <PagesPasswordBox
            wrong={wrong}
            onTyping={() => setWrong(false)}
            onOpen={(value) => {
              setWrong(false)
              setTyped(value)
            }}
          />
        )}
      </div>
    )
  }
  if (state.kind === 'error') {
    return (
      <Alert tone="error" className={className}>
        {state.reason}{' '}
        <Button variant="link" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </Button>
      </Alert>
    )
  }

  const onPage = boxed ? marks.filter((m) => m.page === current) : []
  const elsewhere = [...new Set(marks.filter((m) => m.selected && m.page !== current).map((m) => m.page))]
  const go = (n: number) => setShown(Math.min(Math.max(1, n), pageCount))
  const label = `Page ${current} of ${doc.title}`
  // The page as it lies, placed and turned; `max-width: none` against the base style's 100 %, which would squeeze a turned page.
  const placed = layout ? { ...sourceStyle(layout), maxWidth: 'none' } : undefined
  const note = drawn ? turnText(turn) : null

  return (
    <div className={className}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-1">
          <Button variant="ghost" size="sm" disabled={current <= 1} onClick={() => go(current - 1)} aria-label="Previous page">
            <ChevronLeft size={16} aria-hidden />
          </Button>
          <span className="text-foreground font-medium">
            Page {current} <span className="text-muted-foreground font-normal">of {pageCount}</span>
          </span>
          <Button variant="ghost" size="sm" disabled={current >= pageCount} onClick={() => go(current + 1)} aria-label="Next page">
            <ChevronRight size={16} aria-hidden />
          </Button>
        </span>
        {elsewhere.length > 0 && (
          <Button variant="link" onClick={() => go(elsewhere[0]!)}>
            The selected field is on page {elsewhere[0]}
          </Button>
        )}
      </div>
      <div ref={frame} className="w-full overflow-auto">
        {drawing?.kind === 'none' ? (
          <div className="flex h-[30vh] items-center justify-center p-6 text-center" data-evidence-page={current}>
            <p className="text-muted-foreground max-w-sm text-sm">{drawing.reason === 'missing' ? PAGE_MISSING : PAGE_UNREADABLE}</p>
          </div>
        ) : drawing?.kind === 'error' ? (
          <Alert tone="error">
            {drawing.reason}{' '}
            <Button variant="link" onClick={() => drawPage(current, true)}>
              Try again
            </Button>
          </Alert>
        ) : previewing && !image ? (
          // The engine is drawing it: its place, the page's own size where `pages[]` gives it.
          <SkeletonRegion label={`Drawing page ${current}`}>
            {layout ? (
              <div className="border-border mx-auto box-content overflow-hidden border" style={layout.frame} data-evidence-drawing={current}>
                <Skeleton className="h-full w-full rounded-none" />
              </div>
            ) : (
              <Skeleton className="h-[60vh] w-full rounded-lg" />
            )}
          </SkeletonRegion>
        ) : (
          <div
            className="border-border relative mx-auto box-content overflow-hidden border"
            style={layout ? layout.frame : undefined}
            data-evidence-page={current}
            data-rotate={layout?.rotate ?? 0}
          >
            {image ? (
              <img src={image.url} alt={label} className="absolute block" style={placed} data-evidence-source={previewing ? 'preview' : 'image'} />
            ) : (
              <canvas ref={canvas} className="absolute block" style={placed} aria-label={label} data-evidence-source="pdf" />
            )}
            {layout &&
              onPage.map((mark) => (
                <div
                  key={mark.key}
                  data-evidence-box={mark.key}
                  title={mark.label}
                  aria-hidden
                  className={cn('pointer-events-none absolute rounded-sm border-2', mark.selected ? 'border-primary bg-primary/15' : 'border-primary/50 bg-primary/5')}
                  style={boxStyle(mark.box, layout.scale)}
                />
              ))}
          </div>
        )}
      </div>
      {note && <p className="text-muted-foreground mt-2 text-xs">{note}</p>}
    </div>
  )
}

/**
 * The box that opens an encrypted PDF's pages **in this browser only** (D56):
 * the password goes to pdf.js here and nowhere else — no parse, no request but
 * the file's own logged read. Shared by the evidence page and the preview.
 * Emptied after every try; a wrong one is said, never kept.
 */
export function PagesPasswordBox({ wrong, onOpen, onTyping, busy = false }: { wrong: boolean; onOpen: (password: string) => void; onTyping?: () => void; busy?: boolean }) {
  const id = useId()
  const [value, setValue] = useState('')
  const submit = () => {
    if (!value) return
    onOpen(value)
    setValue('')
  }
  return (
    <div className="flex flex-col items-center gap-1.5" data-pages-password>
      <span className="flex flex-wrap items-center justify-center gap-2">
        <label htmlFor={id} className="text-foreground flex items-center gap-1.5 text-sm font-medium">
          <LockKeyhole size={14} aria-hidden />
          Password
        </label>
        <Input
          id={id}
          size="sm"
          type="password"
          autoComplete="off"
          className="w-44"
          value={value}
          aria-invalid={wrong ? true : undefined}
          onChange={(e) => {
            setValue(e.target.value)
            onTyping?.()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
        />
        <Button size="sm" variant="outline" disabled={!value || busy} onClick={submit}>
          Open the pages
        </Button>
      </span>
      {wrong && (
        <span className={cn('text-xs', TONE_TEXT.error)} role="alert">
          That password does not open it — try again.
        </span>
      )}
    </div>
  )
}
